<template>
  <div class="product-card h-[475px] items-end">
    <div class="absolute right-[30px] top-[30px] z-[3]">
      <AddToList custom-class="w-8 h-8" :product="product" tooltip-placement="left" />
    </div>

    <router-link :to="link" @click="$emit('linkClick', $event)">
      <div class="product-card__image relative">
        <InnoDiscountBadge :properties="product.properties!" />
        <VcImage
          :src="product.imgSrc"
          :alt="product.name"
          class="w-full cursor-pointer rounded-xl object-contain object-center p-2"
          lazy
        />
      </div>
    </router-link>

    <div class="product-card__content relative flex flex-col justify-end">
      <router-link
        :to="link"
        :title="product.name"
        class="line-clamp-2 w-full text-lg font-bold text-[color:var(--color-text)]"
        @click="$emit('linkClick', $event)"
      >
        {{ product.name }}
      </router-link>

      <!-- <div v-if="product.description?.content">
        {{ product.description?.content }}
      </div> -->

      <div v-if="descriptionProperty" class="line-clamp-3">
        {{ descriptionProperty }}
      </div>

      <div v-if="isAuthenticated" class="pt-2 flex flex-wrap items-end justify-between">
        <div class="mt-2 flex flex-col">
          <VcItemPrice :properties="product.properties" class="mb-2 text-xl" :value="product.price" />
          <InnoInStock
            :stock-type="product.sapStockStatus"
            :is-in-stock="product.availabilityData?.isInStock"
            :quantity="product.availabilityData?.availableQuantity"
          />
        </div>
        <InnoAddToCart large :product="product" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useRouter } from "vue-router";
import { getProductRoute } from "@/core/utilities";
import { useUser } from "@/shared/account";
import { InnoAddToCart } from "@/shared/cart";
import InnoDiscountBadge from "./inno-discount-badge.vue";
import { AddToList } from "@/shared/wishlists";
import type { Product } from "@/core/api/graphql/types";
import type { PropType } from "vue";
import type { RouteLocationRaw } from "vue-router";
import InnoInStock from "./inno-in-stock.vue";

defineEmits<{ (eventName: "linkClick", globalEvent: PointerEvent): void }>();

const props = defineProps({
  product: {
    type: Object as PropType<Product>,
    required: true,
  },
});

const { isAuthenticated } = useUser();

const link = computed<RouteLocationRaw>(() => getProductRoute(props.product.id, props.product.slug));
const router = useRouter();

const descriptionProperty = computed(
  () => props.product?.properties?.filter((item) => item.name === "omschrijving_property")[0]?.value ?? "",
);
</script>

<style lang="scss">
.inno-carousel {
  .product-card {
    @apply bg-white;
  }

  @media (min-width: 768px) {
    &__wrapper::after {
      content: "";
      @apply absolute left-0 top-0 z-0 rounded-xl bg-white;
      width: calc(50% - 50px);
      height: 475px;
    }

    .swiper-slide {
      width: calc(25% - 10px);
      height: 475px;
    }

    .swiper-slide-prev {
      @apply w-1/2;
      height: 475px;
    }

    .swiper-slide-active {
      @apply w-1/2;
      height: 475px;
    }

    .swiper-slide {
      .product-card {
        @apply flex flex-col items-stretch overflow-hidden rounded-xl;

        > a {
          @apply flex-1 min-h-0 block overflow-hidden;
        }

        &__image {
          @apply flex h-full items-center justify-center overflow-hidden p-5;

          img {
            @apply w-full max-h-full object-contain;
          }
        }

        &__content {
          @apply shrink-0 p-[30px] pt-0;
        }
      }
    }
  }

  @media (min-width: 1280px) {
    .swiper-slide-prev,
    .swiper-slide-active {
      .product-card {
        @apply flex flex-row min-h-full justify-between m-0 overflow-hidden rounded-xl;

        > a {
          @apply block w-1/2 shrink-0 overflow-hidden;
        }

        &__image {
          @apply flex justify-center;

          img {
            height: 475px;
          }
        }

        &__content {
          @apply shrink-0 p-5 pt-0 w-1/2 h-full;
        }
      }
    }
  }

  @media (min-width: 1200px) {
    .swiper-slide-prev,
    .swiper-slide-active {
      .product-card {
        &__content {
          @apply p-[30px] pt-0;
        }
      }
    }
  }

  @media (max-width: 767px) {
    .swiper-slide-prev,
    .swiper-slide-active {
      .product-card {
        @apply flex flex-col overflow-hidden rounded-xl;

        > a {
          @apply flex flex-col flex-1 min-h-0;
        }

        &__image {
          @apply flex w-full flex-1 min-h-0 items-center justify-center overflow-hidden;

          img {
            @apply w-full max-h-full object-contain;
          }
        }

        &__content {
          @apply shrink-0 p-5 pt-0;
        }
      }
    }
  }
}
</style>
